import { useCallback, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import { PaginatedResponse } from '@/types/responses';

const SWR_OPTIONS = {
    revalidateOnFocus: false,
    revalidateFirstPage: false,
    dedupingInterval: 5000,
} as const;

interface UseOccurrencesParams<T> {
    entityId: string | undefined;
    cacheKeyPrefix: string;
    fetcher: (
        id: string,
        params: { page: number; size: number },
    ) => Promise<PaginatedResponse<T>>;
    pageSize?: number;
}

export function useOccurrences<T>({
    entityId,
    cacheKeyPrefix,
    fetcher,
    pageSize = 20,
}: UseOccurrencesParams<T>) {
    const getKey = useCallback(
        (pageIndex: number, previousPageData: PaginatedResponse<T> | null) => {
            if (!entityId) return null;
            if (previousPageData && previousPageData.items.length < pageSize)
                return null;
            return [
                `${cacheKeyPrefix}-${entityId}-${pageSize}`,
                pageIndex,
            ] as const;
        },
        [entityId, cacheKeyPrefix, pageSize],
    );

    const { data, error, size, setSize, isLoading, mutate } = useSWRInfinite<
        PaginatedResponse<T>
    >(
        getKey,
        ([, pageIndex]) =>
            entityId
                ? fetcher(entityId, {
                      page: (pageIndex as number) + 1,
                      size: pageSize,
                  })
                : Promise.resolve({
                      items: [],
                      page: {
                          page: 1,
                          size: pageSize,
                          totalItems: 0,
                          totalPages: 0,
                      },
                  }),
        SWR_OPTIONS,
    );

    const occurrences = useMemo(
        () => (data ? data.flatMap((page) => page.items) : []),
        [data],
    );

    const hasMore = useMemo(() => {
        if (!data || data.length === 0) return false;
        const lastPage = data[data.length - 1];
        return lastPage.page.page < lastPage.page.totalPages;
    }, [data]);

    const isLoadingMore =
        size > 0 && !!data && typeof data[size - 1] === 'undefined';

    const loadMore = useCallback(() => {
        if (!isLoadingMore && hasMore) {
            void setSize(size + 1);
        }
    }, [isLoadingMore, hasMore, setSize, size]);

    return {
        data: occurrences,
        error,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        mutate,
    };
}
